import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useGoogleLogin } from '@react-oauth/google';
import { usePostHog } from 'posthog-js/react';
import { connectGoogleIntegration, disconnectGoogleAds, refreshGoogleAdsAccounts } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { GOOGLE_SERVICES_STATUS_QUERY_KEY, googleIntegrationScopesQueryOptions } from '@/lib/query-options';

/**
 * The Google Ads connection lifecycle (#6668): connect and disconnect, owned by the
 * Integrations card and the campaign-page connection banner. Connect is the OAuth popup:
 * exchange the auth code (JWT-anchored, #4152), re-scan accessible accounts, then invalidate
 * the profile and accounts queries so every surface reflects the new state. `onConnected`
 * fires after that, so the caller can carry straight on to choosing the account. A revoked
 * token renders as not connected, so reconnecting is the same Connect button.
 */
export function useGoogleAdsConnection({ onConnected }: { onConnected?: () => void } = {}) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  // The backend owns the scope list (#6879): Google Ads, plus Analytics and Tag Manager behind its flag. The
  // Google Ads connection is still the refresh token on the user row.
  const { data: consent } = useSuspenseQuery(googleIntegrationScopesQueryOptions());

  const invalidateConnectionState = () => {
    // /user/me carries google_ads_connected + google_connection_broken (#5935/#5936);
    // the accounts list drives the picker and the top-bar switcher.
    queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
    queryClient.invalidateQueries({ queryKey: ['googleAdsAccounts'] });
    // Connect stores whichever of Analytics and Tag Manager the same consent granted; disconnect revokes the whole
    // grant at Google, which breaks them (#6879).
    queryClient.invalidateQueries({ queryKey: GOOGLE_SERVICES_STATUS_QUERY_KEY });
  };

  const oauthLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        await connectGoogleIntegration(codeResponse.code);
        await refreshGoogleAdsAccounts();
        posthog?.capture('google_ads_reconnected');
        invalidateConnectionState();
        onConnected?.();
      } catch (error) {
        toast({
          title: 'Connection Failed',
          description: error instanceof Error ? error.message : 'Failed to connect Google Ads',
          variant: 'destructive',
        });
      } finally {
        setIsAuthorizing(false);
      }
    },
    onError: () => setIsAuthorizing(false),
    onNonOAuthError: () => setIsAuthorizing(false),
    scope: consent.scope,
    flow: 'auth-code',
    include_granted_scopes: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleAds,
    onSuccess: () => {
      posthog?.capture('google_ads_disconnected');
      toast({ title: 'Google Ads Disconnected' });
      invalidateConnectionState();
    },
    onError: (error: Error) => {
      toast({
        title: 'Disconnect Failed',
        description: error.message || 'Failed to disconnect Google Ads',
        variant: 'destructive',
      });
    },
  });

  const connect = () => {
    setIsAuthorizing(true);
    oauthLogin();
  };

  return {
    connect,
    disconnect: () => disconnectMutation.mutate(),
    isAuthorizing,
    isDisconnecting: disconnectMutation.isPending,
  };
}
